import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { objectOf, any, func, bool } from 'prop-types';
import JsonViewer from 'react-json-view';
import Geohash from 'latlon-geohash';
import { useParams } from 'react-router-dom';

import {
  MapContainer,
  Rectangle,
  Marker,
  Popup,
  TileLayer,
  LayerGroup,
  GeoJSON,
  FeatureGroup
} from 'react-leaflet';

import {
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider
} from '@mui/material';
import LoadingSplashScreen from '../../components/LoadingSplashScreen';

import { getOrder, getMission } from 'actions/orderActions';
import { geoHashFromGeoJson, parseDate } from '../../utils/parsingUtils';
import Page from '../../components/Page/Page';

const ShowOrder = props => {
  const [missionId, setMissionId] = useState(null);
  const [doesStartFinishOverlap, setdoesStartFinishOverlap] = useState(false);
  const [dropIndex, setDropIndex] = useState(0);
  const params = useParams();

  const { order, requestOrder, requestMission, loading } = props;

  useEffect(() => {
    if (order && order.mission && order.mission.geoJson) {
      var features = order.mission.geoJson.features;
      features.forEach((entry, index) => {
        var type = entry.properties.type;
        if (type === 'drop') {
          setDropIndex(index);
        }
      });
      if (
        features[0].geometry.coordinates[0] ===
          features[features.length - 1].geometry.coordinates[0] &&
        features[0].geometry.coordinates[1] ===
          features[features.length - 1].geometry.coordinates[1]
      ) {
        setdoesStartFinishOverlap(true);
      }
    }
  }, [order]);

  // Initial Order
  useEffect(() => {
    params?.id && requestOrder(params?.id);
  }, [requestOrder, params]);

  useEffect(() => {
    if (order && order.missionId) {
      setMissionId(order.missionId);
    }
  }, [order]);

  // Request mission if order has one
  useEffect(() => {
    if (missionId) {
      requestMission(missionId);
    }
  }, [requestMission, missionId]);

  let i = 0;

  return (
    <Page style={{
      padding: '24px'
    }}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          {order && Object.keys(order).length > 0 && (
            <Card>
              <LoadingSplashScreen
                show={!order.mission && !loading}
                icon="warning"
                showProgress={false}
                content="Order information not complete. Please refresh."
              />
              {order.mission?.geoJson && (
                <CardContent>
                  <MapContainer
                    center={order.startLatlng.split(',')}
                    style={{ height: '500px' }}
                    zoom={16}
                    maxZoom={24}>
                    <TileLayer
                      attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LayerGroup>
                      {order.mission && (
                        <>
                          {/* Reserved airspace */}
                          <FeatureGroup>
                            {geoHashFromGeoJson(order.mission.geoJson)
                              .filter(
                                (value, index, self) =>
                                  self.indexOf(value) === index
                              ) // eslint-disable-line max-len
                              .map(geohash => {
                                const geoBounds = Geohash.bounds(geohash); // eslint-disable-line max-len
                                const bounds = [geoBounds.sw, geoBounds.ne]; // eslint-disable-line max-len
                                return (
                                  <Rectangle
                                    bounds={bounds}
                                    color="yellow"
                                    opacity={0.5}
                                    fillOpacity={0.5}
                                    key={geohash}
                                  />
                                );
                              })}
                          </FeatureGroup>
                          {/* Path */}
                          <GeoJSON
                            data={JSON.parse(order.mission.geoJsonLineString)}
                          />
                          {/* Start Point */}
                          {order.startLatlng && (
                            <Marker position={order.startLatlng.split(',')}>
                              <Popup>Start position</Popup>
                            </Marker>
                          )}
                          {/* Drop Point */}
                          {order.dropLatlng && (
                            <Marker position={order.dropLatlng.split(',')}>
                              <Popup>Drop position</Popup>
                            </Marker>
                          )}
                          {/* Finish Point */}
                          {order.finishLatlng && (
                            <Marker position={order.finishLatlng.split(',')}>
                              <Popup>Finish position</Popup>
                            </Marker>
                          )}
                        </>
                      )}
                    </LayerGroup>
                  </MapContainer>
                </CardContent>
              )}
            </Card>
          )}
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={3}>
            {order ? (
              <>
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="Order Status" />
                    <Divider />
                    <CardContent>
                      <Typography variant="body1">
                        <strong>Status: </strong>
                        {`${order.status}`}
                      </Typography>
                      {order.basket && order.basket.items && (
                        <>
                          <Typography variant="body1">
                            <strong>basket: </strong>
                          </Typography>
                          <ul style={{paddingLeft: '2em'}}>
                            {order.basket.items.map(item => (
                              <li>
                                <Typography variant="body1">{`${item.name}, ${item.description}`}</Typography>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      <Typography variant="body1">
                        <strong>Created: </strong>
                        {`${parseDate(order.createDate)}`}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Modified: </strong>
                        {`${parseDate(order.modifyDate)}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="Order QR Code" />
                    <Divider />
                    <CardContent>
                      <img
                        alt="QR Code"
                        src={`data:image/png;base64, ${order.base64QrCode}`}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : null}
            {order && (
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Order Details" />
                  <Divider />
                  <CardContent>
                    <JsonViewer
                      src={order}
                      collapsed={1}
                      enableClipboard={false}
                      displayObjectSize={false}
                      displayDataTypes={false}
                      collapseStringsAfterLength={64}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
            {order?.mission?.flightData &&
              Array.isArray(order.mission.flightData) &&
              order.mission.flightData.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="Flight Data" />
                    <Divider />
                    <CardContent>
                      <JsonViewer
                        src={order?.mission?.flightData}
                        collapsed={1}
                        enableClipboard={false}
                        displayObjectSize={false}
                        displayDataTypes={false}
                        collapseStringsAfterLength={64}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
          </Grid>
        </Grid>
      </Grid>
    </Page>
  );
};

const mapStateToProps = state => ({
  order: state.orders.orderDetails,
  loading: state.ui.loading
});

const mapDispatchToProps = dispatch => ({
  requestOrder: orderId => dispatch(getOrder(orderId)),
  requestMission: missionId => dispatch(getMission(missionId))
});

ShowOrder.propTypes = {
  mission: objectOf(any),
  requestOrder: func.isRequired,
  requestMission: func.isRequired,
  loading: bool.isRequired
};

ShowOrder.defaultProps = {
  mission: {}
};

export default connect(mapStateToProps, mapDispatchToProps)(ShowOrder);
